import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { setAuthTokenGetter } from "@workspace/api-client-react";
import { normalizeBackendUrl } from "@/lib/api";
import { fetchCurrentUser, type AuthUser } from "@/lib/authSession";

export type { AuthUser };

const TOKEN_KEY = "token";

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL as string | undefined);

export function triggerForceLogout() {
  localStorage.removeItem(TOKEN_KEY);
  window.location.href = "/";
}

function readToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export interface TwoFaChallenge {
  requires2fa: true;
  method: "email" | "totp";
  challengeToken: string;
  userId: number;
}

/** Result of GIS credential exchange (login or signup). */
export type GoogleCredentialResult =
  | TwoFaChallenge
  | { requiresApproval: true; message: string }
  | void;

interface AuthContextValue {
  isAuthenticated: boolean;
  user: AuthUser | null;
  sessionHydrating: boolean;
  sessionHydrateError: string | null;
  needs2faSetup: boolean;
  set2faSetupDone: () => void;
  hydrateSession: () => Promise<boolean>;
  login: (email: string, password: string, turnstileToken: string, rememberMe: boolean) => Promise<TwoFaChallenge | void>;
  loginWithGoogleCredential: (
    credential: string,
    intent: "login" | "signup",
    rememberMe: boolean,
  ) => Promise<GoogleCredentialResult>;
  completeLogin: (
    userId: number,
    challengeToken: string,
    code: string,
    rememberMe: boolean,
    isRecoveryCode?: boolean,
  ) => Promise<void>;
  resendOtp: (userId: number, challengeToken: string) => Promise<void>;
  clearClientSession: () => void;
  logout: () => void;
  updateUser: (partial: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => readToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [needs2faSetup, setNeeds2faSetup] = useState(false);
  const [sessionHydrating, setSessionHydrating] = useState(() => !!readToken());
  const [sessionHydrateError, setSessionHydrateError] = useState<string | null>(null);
  const hydrateInFlightRef = useRef(false);

  useEffect(() => {
    setAuthTokenGetter(() => readToken());
  }, [token]);

  const clearClientSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setNeeds2faSetup(false);
    setSessionHydrating(false);
    setSessionHydrateError(null);
  }, []);

  const applySession = useCallback((
    nextToken: string,
    nextUser: AuthUser | null,
    options?: { requires2faSetup?: boolean },
  ) => {
    localStorage.setItem(TOKEN_KEY, nextToken);
    setToken(nextToken);
    setUser(nextUser);
    setNeeds2faSetup(Boolean(options?.requires2faSetup));
    setSessionHydrating(false);
    setSessionHydrateError(null);
  }, []);

  const hydrateSession = useCallback(async (): Promise<boolean> => {
    const authToken = readToken();
    if (!authToken) {
      clearClientSession();
      return false;
    }
    if (hydrateInFlightRef.current) return !!user;
    hydrateInFlightRef.current = true;
    setToken(authToken);
    setSessionHydrating(true);
    setSessionHydrateError(null);

    const result = await fetchCurrentUser(authToken);
    hydrateInFlightRef.current = false;
    setSessionHydrating(false);

    if (result.ok) {
      setUser(result.user);
      setSessionHydrateError(null);
      return true;
    }
    if (result.unauthorized) {
      clearClientSession();
      return false;
    }
    setSessionHydrateError(result.message);
    return false;
  }, [clearClientSession, user]);

  useEffect(() => {
    if (!token || user) {
      if (!token) setSessionHydrating(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setSessionHydrating(true);
      setSessionHydrateError(null);
      const result = await fetchCurrentUser(token);
      if (cancelled) return;
      setSessionHydrating(false);
      if (result.ok) {
        setUser(result.user);
        setSessionHydrateError(null);
        return;
      }
      if (result.unauthorized) {
        clearClientSession();
        return;
      }
      setSessionHydrateError(result.message);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, user, clearClientSession]);

  const login = useCallback(async (
    email: string,
    password: string,
    turnstileToken: string,
    rememberMe: boolean,
  ): Promise<TwoFaChallenge | void> => {
    const deviceFingerprint = (() => {
      try {
        const key = "cpl_device_fingerprint";
        let fp = localStorage.getItem(key);
        if (!fp) {
          fp = crypto.randomUUID();
          localStorage.setItem(key, fp);
        }
        return fp;
      } catch {
        return undefined;
      }
    })();

    const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        turnstileToken: turnstileToken || undefined,
        rememberMe,
        deviceFingerprint,
      }),
    });

    const data = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok) {
      const err = new Error(
        (data as { error?: string }).error ?? "Login failed. Please try again.",
      ) as Error & { accountLocked?: boolean; captchaRequired?: boolean; retryAfter?: number };
      err.accountLocked = Boolean((data as { accountLocked?: boolean }).accountLocked);
      err.captchaRequired = Boolean((data as { captchaRequired?: boolean }).captchaRequired);
      err.retryAfter = typeof (data as { retryAfter?: number }).retryAfter === "number"
        ? (data as { retryAfter?: number }).retryAfter
        : undefined;
      throw err;
    }

    if ((data as { requires2fa?: boolean }).requires2fa) {
      const challengeToken = String((data as { challengeToken?: string }).challengeToken ?? "").trim();
      if (!challengeToken) {
        throw new Error("Invalid login response: missing challenge. Please try again.");
      }
      const rawId = (data as { userId?: unknown; user?: { id?: unknown } }).userId
        ?? (data as { user?: { id?: unknown } }).user?.id;
      const userId = typeof rawId === "number" && Number.isFinite(rawId) ? rawId : Number(rawId);
      if (!Number.isFinite(userId) || userId <= 0) {
        throw new Error("Invalid login response: missing user. Please try again.");
      }
      return {
        requires2fa: true,
        method: ((data as { method?: "email" | "totp" }).method ?? "email"),
        challengeToken,
        userId,
      };
    }

    const nextToken = (data as { token?: string }).token ?? null;
    if (!nextToken) throw new Error("Missing token in login response");
    const nextUser = ((data as { user?: AuthUser }).user) ?? null;
    applySession(nextToken, nextUser, {
      requires2faSetup: Boolean((data as { requires2faSetup?: boolean }).requires2faSetup),
    });
    if (!nextUser) {
      await hydrateSession();
    }
  }, [applySession, hydrateSession]);

  const loginWithGoogleCredential = useCallback(async (
    credential: string,
    intent: "login" | "signup",
    rememberMe: boolean,
  ): Promise<GoogleCredentialResult> => {
    const deviceFingerprint = (() => {
      try {
        const key = "cpl_device_fingerprint";
        let fp = localStorage.getItem(key);
        if (!fp) {
          fp = crypto.randomUUID();
          localStorage.setItem(key, fp);
        }
        return fp;
      } catch {
        return undefined;
      }
    })();

    const res = await fetch(`${BACKEND_URL}/api/auth/google/credential`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ credential, intent, rememberMe, deviceFingerprint }),
    });

    const data = await res.json().catch(() => ({} as Record<string, unknown>));

    if (res.status === 201 && (data as { requiresApproval?: boolean }).requiresApproval) {
      return {
        requiresApproval: true,
        message: String((data as { message?: string }).message ?? "Registration submitted."),
      };
    }

    if (!res.ok) {
      const err = new Error(
        (data as { error?: string }).error ?? "Google sign-in failed. Please try again.",
      ) as Error & { accountLocked?: boolean; retryAfter?: number };
      err.accountLocked = Boolean((data as { accountLocked?: boolean }).accountLocked);
      if (res.status === 429) {
        const ra = (data as { retryAfter?: number }).retryAfter;
        err.retryAfter = typeof ra === "number" && ra > 0 ? ra : undefined;
      }
      throw err;
    }

    if ((data as { requires2fa?: boolean }).requires2fa) {
      const challengeToken = String((data as { challengeToken?: string }).challengeToken ?? "").trim();
      if (!challengeToken) {
        throw new Error("Invalid response: missing challenge. Please try again.");
      }
      const rawId = (data as { userId?: unknown; user?: { id?: unknown } }).userId
        ?? (data as { user?: { id?: unknown } }).user?.id;
      const userId = typeof rawId === "number" && Number.isFinite(rawId) ? rawId : Number(rawId);
      if (!Number.isFinite(userId) || userId <= 0) {
        throw new Error("Invalid response: missing user. Please try again.");
      }
      return {
        requires2fa: true,
        method: ((data as { method?: "email" | "totp" }).method ?? "email"),
        challengeToken,
        userId,
      };
    }

    const nextToken = (data as { token?: string }).token ?? null;
    if (!nextToken) throw new Error("Missing token in sign-in response");
    const nextUser = ((data as { user?: AuthUser }).user) ?? null;
    applySession(nextToken, nextUser, {
      requires2faSetup: Boolean((data as { requires2faSetup?: boolean }).requires2faSetup),
    });
    if (!nextUser) {
      await hydrateSession();
    }
  }, [applySession, hydrateSession]);

  const completeLogin = useCallback(async (
    userId: number,
    challengeToken: string,
    code: string,
    rememberMe: boolean,
    isRecoveryCode = false,
  ): Promise<void> => {
    if (!Number.isFinite(userId) || userId <= 0 || !challengeToken.trim()) {
      throw new Error("Invalid verification request. Return to login and try again.");
    }
    const deviceFingerprint = (() => {
      try {
        const key = "cpl_device_fingerprint";
        let fp = localStorage.getItem(key);
        if (!fp) {
          fp = crypto.randomUUID();
          localStorage.setItem(key, fp);
        }
        return fp;
      } catch {
        return undefined;
      }
    })();

    const res = await fetch(`${BACKEND_URL}/api/auth/2fa/challenge`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, challengeToken, code, rememberMe, isRecoveryCode, deviceFingerprint }),
    });
    const data = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok) {
      throw new Error((data as { error?: string }).error ?? "Verification failed");
    }

    const nextToken = (data as { token?: string }).token ?? null;
    if (!nextToken) throw new Error("Missing token in verification response");
    const nextUser = ((data as { user?: AuthUser }).user) ?? null;
    applySession(nextToken, nextUser);
    if (!nextUser) {
      await hydrateSession();
    }
  }, [applySession, hydrateSession]);

  const resendOtp = useCallback(async (userId: number, challengeToken: string): Promise<void> => {
    const res = await fetch(`${BACKEND_URL}/api/auth/2fa/email/resend`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, challengeToken }),
    });
    const data = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok) {
      throw new Error((data as { error?: string }).error ?? "Could not resend code");
    }
  }, []);

  const logout = useCallback(() => {
    clearClientSession();
    window.location.href = "/";
  }, [clearClientSession]);

  const set2faSetupDone = useCallback(() => {
    setNeeds2faSetup(false);
  }, []);

  const updateUser = useCallback((partial: Partial<AuthUser>) => {
    setUser(prev => (prev ? { ...prev, ...partial } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!token,
        user,
        sessionHydrating,
        sessionHydrateError,
        needs2faSetup,
        set2faSetupDone,
        hydrateSession,
        login,
        loginWithGoogleCredential,
        completeLogin,
        resendOtp,
        clearClientSession,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
